import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ELoaderSize,
  Grid,
  GridItem,
  Heading,
  Loader,
  Text,
  Thumbnail,
} from '@outdoorsyco/bonfire';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import MaxWidth from '@/components/common/layout/MaxWidth';
import { useRouter } from '@/hooks/useRouter';
import { getUser } from '@/redux/selectors/auth/user';
import { getReferrerInfo } from '@/services/getReferralData';
import { IRefererInfo } from '@/services/types/referral/referral';
import { setItemInLocalStorage } from '@/utility/localstorage';

type TOwnerReferralPageProps = {
  referralCode: string;
};

export const OwnerReferralPage = ({ referralCode }: TOwnerReferralPageProps) => {
  const intl = useIntl();
  const router = useRouter();
  const user = useSelector(getUser);

  const [referrerInfo, setReferrerInfo] = useState<IRefererInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean | undefined>(true);

  useEffect(() => {
    const fetchReferralInfo = async () => {
      try {
        const response = await getReferrerInfo(referralCode);

        if (!response.referral_code) {
          router.push('/', undefined, { shallow: true });
          return;
        }

        setReferrerInfo(response);
        setItemInLocalStorage('owner-referral-code', response.referral_code);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (referralCode) {
      fetchReferralInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode]);

  useEffect(() => {
    if (user && user.owner) {
      router.push('/', undefined, { shallow: true });
    }
  }, [user, router]);

  const buttonLabel = intl.formatMessage({
    defaultMessage: 'List your RV',
    id: 'eRWzNe',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <Loader size={ELoaderSize.Large} />
      </div>
    );
  }

  return (
    <>
      {referrerInfo?.referral_code && !user?.owner ? (
        <MaxWidth className="mx-auto">
          <Grid columnSpacing={2} className="py-5 md:py-10">
            <GridItem sm={12}>
              <div className="flex flex-col gap-6 md:flex-row-reverse md:gap-12">
                <div className="flex-1 [&>div>img]:!rounded-none">
                  <Thumbnail imageUrl="https://outdoorsy-assets.s3.us-west-2.amazonaws.com/static-assets/images/renter/referral-host-photo.png" />
                </div>
                <div className="flex items-center flex-1">
                  <div className="flex flex-col gap-6">
                    <Heading variant={EHeadingStyleVariant.H4}>
                      {referrerInfo.name ? (
                        <FormattedMessage
                          defaultMessage="You just received $50 off your first booking from {name}!"
                          id="ePw7Qo"
                          values={{ name: referrerInfo?.name }}
                        />
                      ) : (
                        <FormattedMessage
                          defaultMessage="You just received $50 off your first booking!"
                          id="bsTBYn"
                        />
                      )}
                    </Heading>
                    <Text className="text-center md:text-left leading-[30px] md:leading-[24px]">
                      <FormattedMessage
                        defaultMessage="Congratulations! Once you list your RV, your referral credit will automatically be applied to your account. You can use this toward your next trip or to help offset the cost of booking fees."
                        id="AirmZe"
                      />
                    </Text>
                    <div className="flex justify-center md:justify-start">
                      <Button
                        onClick={() => {
                          router.push('/become-a-host', undefined, { shallow: true });
                        }}
                        label={buttonLabel}
                        variant={EButtonColorVariant.Primary}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </GridItem>
          </Grid>
        </MaxWidth>
      ) : null}
    </>
  );
};
